import {
	getModerationFeed,
	getModerationStories,
	getModerationStats,
	getUserModerationHistory,
	takedownContent,
	restoreContent,
	blockUserPublishing,
	unblockUserPublishing,
	getBlockedUsers,
} from '@/services/community/moderation'

export default {
	namespaced: true,
	state: {
		// Feed (infinite scroll) — items são concatenados
		feedItems: [],
		feedMeta: { current_page: 0, last_page: 1, total: 0 },
		feedStatus: 'init',

		// List (tabela paginada)
		listItems: { data: [], meta: { total: 0 } },
		listStatus: 'init',

		// Stories (não sincronizadas no feed — endpoint próprio)
		storyItems: [],
		storyStatus: 'init',

		// Stories — listagem paginada da tela "view all" (mesma separação
		// dashboard vs. lista já usada para feedItems/listItems)
		storyListItems: { data: [], meta: { total: 0 } },
		storyListStatus: 'init',

		// Stats do dashboard
		stats: {},
		statsStatus: 'init',

		// Histórico de moderação do usuário em foco (strikes)
		userHistory: null,
		userHistoryStatus: 'init',

		// Usuários bloqueados
		blockedUsers: { data: [], meta: { total: 0 } },
		blockedUsersStatus: 'init',
	},
	getters: {
		getFeedItems: state => state.feedItems,
		getFeedMeta: state => state.feedMeta,
		getFeedStatus: state => state.feedStatus,

		getListItems: state => state.listItems,
		getListStatus: state => state.listStatus,

		getStoryItems: state => state.storyItems,
		getStoryStatus: state => state.storyStatus,

		getStoryListItems: state => state.storyListItems,
		getStoryListStatus: state => state.storyListStatus,

		getStats: state => state.stats,
		getStatsStatus: state => state.statsStatus,

		getUserHistory: state => state.userHistory,
		getUserHistoryStatus: state => state.userHistoryStatus,

		getBlockedUsers: state => state.blockedUsers,
		getBlockedUsersStatus: state => state.blockedUsersStatus,
	},
	mutations: {
		APPEND_FEED_ITEMS(state, { data, meta }) {
			const existingKeys = new Set(state.feedItems.map(i => `${i.content_type}-${i.id}`))
			const newItems = data.filter(i => !existingKeys.has(`${i.content_type}-${i.id}`))
			state.feedItems = [...state.feedItems, ...newItems]
			state.feedMeta = meta
		},
		RESET_FEED(state) {
			state.feedItems = []
			state.feedMeta = { current_page: 0, last_page: 1, total: 0 }
		},
		SET_FEED_STATUS(state, value) {
			state.feedStatus = value
		},
		SET_LIST_ITEMS(state, value) {
			state.listItems = value
		},
		SET_LIST_STATUS(state, value) {
			state.listStatus = value
		},
		SET_STORY_ITEMS(state, value) {
			state.storyItems = value
		},
		SET_STORY_STATUS(state, value) {
			state.storyStatus = value
		},
		REMOVE_ITEM_FROM_STORIES(state, { content_id }) {
			state.storyItems = state.storyItems.filter(item => item.id !== content_id)
		},
		SET_STORY_LIST_ITEMS(state, value) {
			state.storyListItems = value
		},
		SET_STORY_LIST_STATUS(state, value) {
			state.storyListStatus = value
		},
		REMOVE_ITEM_FROM_STORY_LIST(state, { content_id }) {
			if (state.storyListItems.data) {
				state.storyListItems.data = state.storyListItems.data.filter(item => item.id !== content_id)
			}
		},
		SET_STATS(state, value) {
			state.stats = value
		},
		SET_STATS_STATUS(state, value) {
			state.statsStatus = value
		},
		SET_USER_HISTORY(state, value) {
			state.userHistory = value
		},
		SET_USER_HISTORY_STATUS(state, value) {
			state.userHistoryStatus = value
		},
		SET_BLOCKED_USERS(state, value) {
			state.blockedUsers = value
		},
		SET_BLOCKED_USERS_STATUS(state, value) {
			state.blockedUsersStatus = value
		},
		REMOVE_FROM_BLOCKED_USERS(state, uuid) {
			if (state.blockedUsers.data) {
				state.blockedUsers.data = state.blockedUsers.data.filter(u => u.uuid !== uuid)
				if (state.blockedUsers.meta) state.blockedUsers.meta.total = Math.max(0, state.blockedUsers.meta.total - 1)
			}
		},
		REMOVE_ITEM_FROM_FEED(state, { content_type, content_id }) {
			state.feedItems = state.feedItems.filter(
				item => !(item.content_type === content_type && item.id === content_id),
			)
		},
		REMOVE_ITEM_FROM_LIST(state, { content_type, content_id }) {
			if (state.listItems.data) {
				state.listItems.data = state.listItems.data.filter(
					item => !(item.content_type === content_type && item.id === content_id),
				)
			}
		},
		// Atualiza flag de bloqueio no item do feed/list sem refetch
		UPDATE_ITEM_BLOCK_STATUS(state, { user_uuid, is_publishing_blocked }) {
			state.feedItems = state.feedItems.map(item =>
				item.author && item.author.uuid === user_uuid
					? { ...item, author: { ...item.author, is_publishing_blocked } }
					: item,
			)
			if (state.listItems.data) {
				state.listItems.data = state.listItems.data.map(item =>
					item.author && item.author.uuid === user_uuid
						? { ...item, author: { ...item.author, is_publishing_blocked } }
						: item,
				)
			}
			state.storyItems = state.storyItems.map(item =>
				item.author && item.author.uuid === user_uuid
					? { ...item, author: { ...item.author, is_publishing_blocked } }
					: item,
			)
			if (state.storyListItems.data) {
				state.storyListItems.data = state.storyListItems.data.map(item =>
					item.author && item.author.uuid === user_uuid
						? { ...item, author: { ...item.author, is_publishing_blocked } }
						: item,
				)
			}
		},
	},
	actions: {
		async fetchFeed({ commit, state }, params) {
			if (state.feedStatus === 'loading') return
			commit('SET_FEED_STATUS', 'loading')
			try {
				const { data } = await getModerationFeed(params)
				commit('APPEND_FEED_ITEMS', data)
				commit('SET_FEED_STATUS', 'init')
				return data
			} catch (error) {
				commit('SET_FEED_STATUS', 'error')
				// Don't re-throw — 404 means the backend endpoint isn't ready yet
			}
		},
		resetFeed({ commit }) {
			commit('RESET_FEED')
		},
		async fetchList({ commit }, params) {
			commit('SET_LIST_STATUS', 'pending')
			commit('SET_LIST_ITEMS', { data: [], meta: { total: 0 } })
			try {
				const { data } = await getModerationFeed(params)
				commit('SET_LIST_STATUS', 'init')
				commit('SET_LIST_ITEMS', data)
				return data
			} catch (error) {
				commit('SET_LIST_STATUS', 'error')
			}
		},
		async fetchStories({ commit }, params) {
			commit('SET_STORY_STATUS', 'loading')
			try {
				const { data } = await getModerationStories(params)
				commit('SET_STORY_ITEMS', data.data || [])
				commit('SET_STORY_STATUS', 'init')
				return data
			} catch (error) {
				commit('SET_STORY_STATUS', 'error')
			}
		},
		async fetchStoriesList({ commit }, params) {
			commit('SET_STORY_LIST_STATUS', 'pending')
			commit('SET_STORY_LIST_ITEMS', { data: [], meta: { total: 0 } })
			try {
				const { data } = await getModerationStories(params)
				commit('SET_STORY_LIST_STATUS', 'init')
				commit('SET_STORY_LIST_ITEMS', data)
				return data
			} catch (error) {
				commit('SET_STORY_LIST_STATUS', 'error')
			}
		},
		async fetchStats({ commit }, params) {
			commit('SET_STATS_STATUS', 'pending')
			try {
				const { data } = await getModerationStats(params)
				commit('SET_STATS_STATUS', 'init')
				commit('SET_STATS', data)
				return data
			} catch (error) {
				commit('SET_STATS_STATUS', 'error')
				// Don't re-throw — stats are optional; show zeros
			}
		},
		async fetchUserHistory({ commit }, userUuid) {
			commit('SET_USER_HISTORY', null)
			commit('SET_USER_HISTORY_STATUS', 'pending')
			try {
				const { data } = await getUserModerationHistory(userUuid)
				commit('SET_USER_HISTORY_STATUS', 'init')
				commit('SET_USER_HISTORY', data)
				return data
			} catch (error) {
				commit('SET_USER_HISTORY_STATUS', 'error')
			}
		},
		/**
		 * Remoção completa: remove conteúdo + registra strike + envia notificação WebSocket
		 * + Firebase push + bloqueia publicação (se solicitado). Tudo via um único endpoint.
		 */
		async takedownContent({ commit }, params) {
			try {
				const { data } = await takedownContent(params)
				commit('REMOVE_ITEM_FROM_FEED', params)
				commit('REMOVE_ITEM_FROM_LIST', params)
				commit('REMOVE_ITEM_FROM_STORIES', params)
				commit('REMOVE_ITEM_FROM_STORY_LIST', params)
				if (params.block_publishing && params.author_uuid) {
					commit('UPDATE_ITEM_BLOCK_STATUS', {
						user_uuid: params.author_uuid,
						is_publishing_blocked: true,
					})
				}
				return data
			} catch (error) {
				throw error
			}
		},
		async restoreContent({ commit }, params) {
			try {
				const { data } = await restoreContent(params)
				return data
			} catch (error) {
				throw error
			}
		},
		async blockUserPublishing({ commit }, { userUuid, reason }) {
			try {
				const { data } = await blockUserPublishing(userUuid, { reason })
				commit('UPDATE_ITEM_BLOCK_STATUS', { user_uuid: userUuid, is_publishing_blocked: true })
				return data
			} catch (error) {
				throw error
			}
		},
		async unblockUserPublishing({ commit }, userUuid) {
			try {
				const { data } = await unblockUserPublishing(userUuid)
				commit('UPDATE_ITEM_BLOCK_STATUS', { user_uuid: userUuid, is_publishing_blocked: false })
				commit('REMOVE_FROM_BLOCKED_USERS', userUuid)
				return data
			} catch (error) {
				throw error
			}
		},
		async fetchBlockedUsers({ commit }, params) {
			commit('SET_BLOCKED_USERS_STATUS', 'pending')
			commit('SET_BLOCKED_USERS', { data: [], meta: { total: 0 } })
			try {
				const { data } = await getBlockedUsers(params)
				commit('SET_BLOCKED_USERS', data)
				commit('SET_BLOCKED_USERS_STATUS', 'init')
				return data
			} catch (error) {
				commit('SET_BLOCKED_USERS_STATUS', 'error')
			}
		},
	},
}
